/**
 * The Recorded wall's timeline: one lane per camera on the wall, one shared axis, one playhead
 * through all of them.
 *
 *   ┌ [leading: date · range] [select range] [🔍− ━━●━━ 🔍+] ····· [trailing: ▶ skip speed] ┐
 *   │ date · span   │ axis                                                               │
 *   │ ● Camera 1    │ ███████▓▓▓▓███████░░░░░███████      ▓ event   ▒ motion             │
 *   │ ● Camera 2    │ ...                                                                │
 *   ├ legend (click = show / hide a kind) │ overview bar (drag to pan)                    ┤
 *
 * Seeking is for the WALL, not a camera — a click or a drag anywhere on the lanes or the axis
 * moves the one clock, and every tile follows. Dragging publishes a throttled stream of seeks so
 * the tiles track the finger, then a final one on release.
 *
 * Range selection: turn on "Select" (or hold Shift) and drag across the lanes. The selection can
 * then be zoomed into, or played — which makes it the page's time range.
 *
 * Colour: a lane's blocks say why the footage was kept (continuous / event / motion / manual,
 * from the segment's trigger), and the event + motion retention windows from `recording_triggers`
 * are drawn over them, so detections stand out on a continuously-recording camera too. The
 * legend toggles each kind; a hidden kind is drawn as neutral "footage", so the lane never lies
 * about what exists.
 *
 * Zoom: wheel over the lanes (around the cursor), or the zoom slider (log scale, far left = the whole range). Pan: shift+wheel,
 * a horizontal wheel, or drag the window on the overview bar underneath. While playing, the view
 * follows the playhead once it leaves the visible window.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import {
  MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ArrowsRightLeftIcon, XMarkIcon, PlayIcon,
} from '@heroicons/react/24/outline';
import type { RecordingSegment, RecordingTriggerWindow } from '../../api/contracts';
import { TRIGGER_COLOURS, TRIGGER_LABELS } from '../recordings/RecordingTimeline';
import type { TileStatus } from './PlaybackWallTile';
import Tooltip, { Tip } from '../common/Tooltip';

export interface TimelineLane {
  key: string;
  label: string;
  /** The camera's accent — the dot its tile shows too. */
  color: string;
  /** undefined = still fetching. */
  segments: RecordingSegment[] | undefined;
  /** Event / motion retention windows — drawn over the footage. */
  triggers?: RecordingTriggerWindow[];
  status?: TileStatus;
}

export interface MultiCameraTimelineProps {
  lanes: TimelineLane[];
  rangeStartMs: number;
  rangeEndMs: number;
  timeMs: number;
  /** `final` is false for the intermediate seeks of a drag. */
  onSeek: (ms: number, final: boolean) => void;
  /** "Play this range" on a selection — the host makes it the time range. */
  onPlayRange?: (startMs: number, endMs: number) => void;
  /**
   * The host's own actions on a selection, after zoom / play / clear — the Recordings page's
   * clip download. `clear` drops the selection (e.g. once the download has started).
   */
  selectionActions?: (selection: { start: number; end: number }, clear: () => void) => ReactNode;
  /** The view follows the playhead — true while playing. */
  follow?: boolean;
  /** Control bar, before the timeline's own controls: the host's readout, date + range. */
  leading?: ReactNode;
  /** Control bar, right-aligned after them: the host's transport. */
  trailing?: ReactNode;
  /** The day being shown. */
  dateLabel?: string;
  className?: string;
}

type Kind = 'continuous' | 'event' | 'motion' | 'manual';
const KINDS: Kind[] = ['continuous', 'event', 'motion', 'manual'];
/** A hidden kind still has footage behind it — drawn in this, not left blank. */
const NEUTRAL = '#64748b';

const LABEL_W = 'w-44 xl:w-52';
const AXIS_H = 30;
const LANE_H = 28;
const MIN_SPAN_MS = 60 * 1000;
const DRAG_SEEK_MS = 300;
const MIN_SELECTION_MS = 5000;


const TICK_STEPS_SEC = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600];
const TARGET_TICKS = 9;

const STATUS_TEXT: Partial<Record<TileStatus, string>> = {
  segments: 'loading…',
  loading: 'loading…',
  buffering: 'buffering…',
  gap: 'no footage here',
  'no-footage': 'no recordings',
  error: 'error',
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  person: 'Person', person_count: 'People count', face: 'Face', intrusion: 'Intrusion', ppe: 'PPE',
  vehicle: 'Vehicle', theft: 'Theft', fall: 'Fall',
};

/** Control-bar pieces: 28px segmented pills, same look as the Recordings transport. */
const PILL = 'inline-flex h-7 shrink-0 items-center overflow-hidden rounded-lg border border-border divide-x divide-border';
const SEG = 'flex h-7 items-center justify-center px-1.5 font-mono text-[11px] text-foreground transition-colors hover:bg-muted ' +
  'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent';

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function fmtTick(ms: number, withSeconds: boolean) {
  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit', minute: '2-digit', ...(withSeconds ? { second: '2-digit' } : {}), hour12: false,
  });
}
function fmtSpan(ms: number) {
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = ms / 60000;
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

const kindOfTrigger = (source?: string): Kind =>
  source === 'motion' ? 'motion' : source === 'manual' || source === 'api' ? 'manual' : 'event';
const kindOfSegment = (trigger?: string): Kind =>
  (KINDS as string[]).includes(trigger || '') ? (trigger as Kind) : 'continuous';

/** Adjacent segments with the same trigger drawn as one block — 288 five-minute divs a day otherwise. */
function blocksFor(segments: RecordingSegment[], vs: number, ve: number) {
  const out: { start: number; end: number; kind: Kind }[] = [];
  const sorted = [...segments]
    .map((s) => ({
      start: new Date(s.start_time).getTime(),
      end: s.status === 'in-progress' ? Math.max(new Date(s.end_time).getTime(), Date.now()) : new Date(s.end_time).getTime(),
      kind: kindOfSegment(s.trigger),
    }))
    .filter((b) => b.end > vs && b.start < ve)
    .sort((a, b) => a.start - b.start);
  for (const b of sorted) {
    const last = out[out.length - 1];
    if (last && last.kind === b.kind && b.start - last.end < 2000) last.end = Math.max(last.end, b.end);
    else out.push({ ...b });
  }
  return out;
}

/** Retention windows in view, overlapping ones of the same kind merged, with what they were for. */
function windowsFor(triggers: RecordingTriggerWindow[], vs: number, ve: number) {
  const out: { start: number; end: number; kind: Kind; count: number; types: Set<string> }[] = [];
  const sorted = triggers
    .map((t) => {
      const at = new Date(t.event_time).getTime();
      const start = t.window_start ? new Date(t.window_start).getTime() : at;
      const end = t.window_end ? new Date(t.window_end).getTime() : at + 10_000;
      return { start, end: Math.max(end, start + 1000), kind: kindOfTrigger(t.source), count: t.trigger_count || 1, type: t.event_type || '' };
    })
    .filter((w) => Number.isFinite(w.start) && w.end > vs && w.start < ve)
    .sort((a, b) => a.start - b.start);
  for (const w of sorted) {
    const last = [...out].reverse().find((o) => o.kind === w.kind);
    if (last && w.start <= last.end + 1000) {
      last.end = Math.max(last.end, w.end);
      last.count += w.count;
      if (w.type) last.types.add(w.type);
    } else {
      out.push({ start: w.start, end: w.end, kind: w.kind, count: w.count, types: new Set(w.type ? [w.type] : []) });
    }
  }
  return out;
}

export default function MultiCameraTimeline({
  lanes,
  rangeStartMs,
  rangeEndMs,
  timeMs,
  onSeek,
  onPlayRange,
  selectionActions,
  follow = false,
  leading,
  trailing,
  dateLabel,
  className = '',
}: MultiCameraTimelineProps) {
  const fullSpan = Math.max(MIN_SPAN_MS, rangeEndMs - rangeStartMs);
  const [view, setView] = useState({ start: rangeStartMs, end: rangeStartMs + fullSpan });
  // A new range is a new picture: show all of it, and forget a selection made in the old one.
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  useEffect(() => {
    setView({ start: rangeStartMs, end: rangeStartMs + fullSpan });
    setSelection(null);
  }, [rangeStartMs, fullSpan]);

  const [shown, setShown] = useState<Set<Kind>>(() => new Set(KINDS));
  const toggleKind = (k: Kind) => setShown((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const span = view.end - view.start;
  const setViewClamped = useCallback((start: number, s: number) => {
    const sp = clamp(s, MIN_SPAN_MS, fullSpan);
    const st = clamp(start, rangeStartMs, rangeStartMs + fullSpan - sp);
    // Same view → same object: the follow effect runs on every playhead tick, and a clamped
    // no-op must not re-render (a playhead outside the range would otherwise loop).
    setView((v) => (v.start === st && v.end === st + sp ? v : { start: st, end: st + sp }));
  }, [rangeStartMs, fullSpan]);

  // ─── Follow the playhead while playing ───
  const draggingRef = useRef<'seek' | 'select' | null>(null);
  useEffect(() => {
    if (!follow || draggingRef.current) return;
    if (timeMs < view.start || timeMs > view.end) setViewClamped(timeMs - span * 0.1, span);
  }, [follow, timeMs, view.start, view.end, span, setViewClamped]);

  // ─── Geometry ───
  const trackRef = useRef<HTMLDivElement>(null);
  const msAt = useCallback((clientX: number) => {
    const el = trackRef.current;
    if (!el) return view.start;
    const r = el.getBoundingClientRect();
    return view.start + clamp((clientX - r.left) / Math.max(1, r.width), 0, 1) * span;
  }, [view.start, span]);
  const pct = (ms: number) => ((ms - view.start) / span) * 100;

  // ─── Scrub, or select ───
  const [selectMode, setSelectMode] = useState(false);
  useEffect(() => {
    if (!selectMode) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectMode(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectMode]);
  const selAnchorRef = useRef(0);
  const lastDragSeekRef = useRef(0);
  const [hover, setHover] = useState<number | null>(null);
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const ms = msAt(e.clientX);
    if (selectMode || e.shiftKey) {
      draggingRef.current = 'select';
      selAnchorRef.current = ms;
      setSelection({ start: ms, end: ms });
      return;
    }
    draggingRef.current = 'seek';
    lastDragSeekRef.current = performance.now();
    onSeek(ms, false);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const ms = msAt(e.clientX);
    setHover(ms);
    if (draggingRef.current === 'select') {
      const a = selAnchorRef.current;
      setSelection({ start: Math.min(a, ms), end: Math.max(a, ms) });
      return;
    }
    if (draggingRef.current !== 'seek') return;
    const now = performance.now();
    if (now - lastDragSeekRef.current >= DRAG_SEEK_MS) {
      lastDragSeekRef.current = now;
      onSeek(ms, false);
    }
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const mode = draggingRef.current;
    draggingRef.current = null;
    if (mode === 'select') {
      const a = selAnchorRef.current;
      const b = msAt(e.clientX);
      // A click, not a drag, clears the selection.
      if (Math.abs(b - a) < MIN_SELECTION_MS) setSelection(null);
      else { setSelection({ start: Math.min(a, b), end: Math.max(a, b) }); setSelectMode(false); }
      return;
    }
    if (mode === 'seek') onSeek(msAt(e.clientX), true);
  };

  // ─── Wheel zoom / pan — non-passive, or the page scrolls instead ───
  const viewRef = useRef(view);
  viewRef.current = view;
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const v = viewRef.current;
      const sp = v.end - v.start;
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        const d = (e.shiftKey ? e.deltaY : e.deltaX) || 0;
        setViewClamped(v.start + (d / 400) * sp, sp);
        return;
      }
      const r = el.getBoundingClientRect();
      const f = clamp((e.clientX - r.left) / Math.max(1, r.width), 0, 1);
      const anchor = v.start + f * sp;
      const next = sp * (e.deltaY > 0 ? 1.25 : 0.8);
      const ns = clamp(next, MIN_SPAN_MS, fullSpan);
      setViewClamped(anchor - f * ns, ns);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [setViewClamped, fullSpan]);

  /** Zoom to a span, keeping the playhead (or the middle) where it is on screen. */
  const zoomTo = (ns: number) => {
    const anchor = timeMs >= view.start && timeMs <= view.end ? timeMs : view.start + span / 2;
    const f = (anchor - view.start) / span;
    const s = clamp(ns, MIN_SPAN_MS, fullSpan);
    setViewClamped(anchor - f * s, s);
  };
  // Slider position ↔ span, on a log scale: 0 = the whole range, 1 = MIN_SPAN_MS.
  const zoomRatio = Math.log(MIN_SPAN_MS / fullSpan);
  const spanToT = (sp: number) => (zoomRatio === 0 ? 0 : clamp(Math.log(sp / fullSpan) / zoomRatio, 0, 1));
  const tToSpan = (t: number) => fullSpan * Math.exp(zoomRatio * t);

  const zoomToSelection = () => {
    if (!selection) return;
    const s = selection.end - selection.start;
    // A little air either side, so the selection's edges are visible.
    setViewClamped(selection.start - s * 0.05, s * 1.1);
    // While playing the view follows the playhead — bring it into the selection, or the view
    // would jump straight back to wherever it is.
    if (timeMs < selection.start || timeMs > selection.end) onSeek(selection.start, true);
  };
  const playSelection = () => {
    if (!selection) return;
    onPlayRange?.(selection.start, selection.end);
    setSelection(null);
  };

  // ─── Ticks ───
  const ticks = useMemo(() => {
    const stepSec = TICK_STEPS_SEC.find((s) => (span / 1000) / s <= TARGET_TICKS) ?? 21600;
    const step = stepSec * 1000;
    const d = new Date(view.start);
    const midnight = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const first = midnight + Math.ceil((view.start - midnight) / step) * step;
    const out: { ms: number; label: string }[] = [];
    for (let t = first; t <= view.end; t += step) out.push({ ms: t, label: fmtTick(t, stepSec < 60) });
    // Minor ticks: 4 between majors.
    const minor: number[] = [];
    for (let t = first - step; t <= view.end; t += step / 5) if (t >= view.start) minor.push(t);
    return { major: out, minor };
  }, [view.start, view.end, span]);

  // ─── Overview (pan) bar ───
  const overviewRef = useRef<HTMLDivElement>(null);
  const panDragRef = useRef<{ x: number; start: number } | null>(null);
  const onOverviewDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = overviewRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    const r = el.getBoundingClientRect();
    const at = rangeStartMs + clamp((e.clientX - r.left) / r.width, 0, 1) * fullSpan;
    // Outside the window: jump the window there first, then drag it.
    let start = view.start;
    if (at < view.start || at > view.end) { start = at - span / 2; setViewClamped(start, span); }
    panDragRef.current = { x: e.clientX, start };
  };
  const onOverviewMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = panDragRef.current;
    const el = overviewRef.current;
    if (!d || !el) return;
    const dx = (e.clientX - d.x) / el.getBoundingClientRect().width;
    setViewClamped(d.start + dx * fullSpan, span);
  };
  const onOverviewUp = () => { panDragRef.current = null; };

  // ─── One-row control bar: the richest tier that fits, measured before paint ───
  // The tier is written straight onto the element (not React state) so each candidate can be
  // measured in the same layout pass — no flash of a wrapped bar, no render loop. Runs every
  // render because any of its contents (a selection, a status note) can change its width;
  // when the current tier still fits and the width is unchanged it is a single read.
  const barRef = useRef<HTMLDivElement>(null);
  const barFitRef = useRef({ width: -1, tier: '' });
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const oneRow = () => el.offsetHeight <= 44;
    const fit = () => {
      for (const tier of ['wide', 'mid', 'narrow', 'tiny']) {
        el.dataset.tier = tier;
        if (oneRow()) break;
      }
      barFitRef.current = { width: el.clientWidth, tier: el.dataset.tier || '' };
    };
    if (el.clientWidth !== barFitRef.current.width || !oneRow() || barFitRef.current.tier !== 'wide') fit();
  });
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === barFitRef.current.width) return;
      for (const tier of ['wide', 'mid', 'narrow', 'tiny']) {
        el.dataset.tier = tier;
        if (el.offsetHeight <= 44) break;
      }
      barFitRef.current = { width: el.clientWidth, tier: el.dataset.tier || '' };
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const playheadIn = timeMs >= view.start && timeMs <= view.end;
  const lanesH = Math.max(1, lanes.length) * LANE_H;
  const colourOf = (k: Kind) => (shown.has(k) ? TRIGGER_COLOURS[k] : NEUTRAL);
  const hasSelection = !!selection && selection.end - selection.start >= MIN_SELECTION_MS;
  const selIn = selection && selection.end > view.start && selection.start < view.end;

  return (
    <div data-tour="playback-timeline" className={`flex flex-col overflow-hidden rounded-lg border border-border bg-card select-none ${className}`}>
      {/* ─── Control bar — ONE row: host date + range | range selection | duration | host transport ─── */}
      <div ref={barRef} className="group/bar flex flex-wrap items-center gap-1 border-b border-border px-1.5 py-1 data-[tier=wide]:gap-1.5">
        {leading}

        {/* Range selection toggle. What to do with a selection floats over the timeline itself
            (see below), so selecting never widens — or wraps — this bar. */}
        <Tip label={selectMode ? 'Cancel selecting (Esc)' : hasSelection ? 'Select a different range — drag across the timeline' : 'Select a range — drag across the timeline (or Shift+drag) to zoom to it, play it or download it'}>
        <button
          onClick={() => setSelectMode((m) => !m)}
          aria-label="Select a time range"
          aria-pressed={selectMode}
          className={`${PILL} ${SEG} gap-1 font-sans font-medium ${
            selectMode ? 'border-brand bg-brand text-white hover:bg-brand-dark' : hasSelection ? 'border-brand text-brand' : ''
          }`}
        >
          <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
        </button>
        </Tip>

        {/* Zoom slider, as on the old Recordings timeline: left = the whole range, right = the
            tightest span. Log scale, so the useful short spans are not crammed into the last few
            pixels. Zooms about the playhead; the wheel over the timeline does the same. */}
        <Tip label={`Zoom — showing ${fmtSpan(span)} of ${fmtSpan(fullSpan)}. Scroll over the timeline to zoom, Shift+scroll to pan.`}>
        <div className="flex h-7 shrink-0 items-center gap-1 px-1">
          <MagnifyingGlassMinusIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
          <input
            type="range"
            min={0}
            max={1000}
            step={1}
            value={Math.round(spanToT(span) * 1000)}
            onChange={(e) => zoomTo(tToSpan(Number(e.target.value) / 1000))}
            aria-label="Timeline zoom"
            className="h-1 w-20 cursor-ew-resize accent-brand group-data-[tier=wide]/bar:w-28"
          />
          <MagnifyingGlassPlusIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
        </div>
        </Tip>

        {trailing && <div className="ml-auto flex flex-wrap items-center justify-end gap-1 group-data-[tier=wide]/bar:gap-1.5">{trailing}</div>}
      </div>

      <div className="flex min-h-0">
        {/* ─── Left column: day + span, then one label per lane ─── */}
        <div className={`${LABEL_W} flex-none border-r border-border`}>
          <div style={{ height: AXIS_H }} className="flex items-center gap-1.5 border-b border-border px-3">
            <Tooltip label={dateLabel} className="min-w-0">
              <span className="min-w-0 truncate text-[11px] font-semibold text-foreground">{dateLabel}</span>
            </Tooltip>
            <span className="flex-none text-[10px] text-muted-foreground">· {fmtSpan(span)}</span>
          </div>
          {lanes.length === 0 ? (
            <div style={{ height: LANE_H }} className="flex items-center px-3 text-[11px] text-muted-foreground">No cameras on the wall</div>
          ) : lanes.map((l) => (
            <div key={l.key} style={{ height: LANE_H }} className="flex items-center gap-2 border-b border-border px-3 last:border-b-0">
              <span className="h-2 w-2 flex-none rounded-full" style={{ background: l.color }} />
              <Tooltip label={l.label} className="min-w-0 flex-1">
                <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-foreground">{l.label}</span>
              </Tooltip>
              {l.status && STATUS_TEXT[l.status] && (
                <span className={`flex-none text-[10px] ${l.status === 'error' ? 'text-red-400' : 'text-muted-foreground'}`}>{STATUS_TEXT[l.status]}</span>
              )}
            </div>
          ))}
        </div>

        {/* ─── Right column: axis + lanes (one scrub surface), playhead across both ─── */}
        <div className="relative min-w-0 flex-1">
          <div
            ref={trackRef}
            className={`relative ${selectMode ? 'cursor-col-resize' : 'cursor-crosshair'}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={() => setHover(null)}
          >
            {/* Axis */}
            <div style={{ height: AXIS_H }} className="relative border-b border-border">
              {ticks.minor.map((t) => (
                <div key={`m${t}`} className="absolute bottom-0 h-1.5 w-px bg-border" style={{ left: `${pct(t)}%` }} />
              ))}
              {ticks.major.map((t) => {
                // Labels are centred on their tick, except at the edges, where centring would
                // push half the label outside the axis.
                const p = pct(t.ms);
                const align = p < 3 ? 'items-start' : p > 97 ? '-translate-x-full items-end' : '-translate-x-1/2 items-center';
                return (
                  <div key={t.ms} className={`absolute bottom-0 flex flex-col ${align}`} style={{ left: `${p}%` }}>
                    <span className="mb-0.5 font-mono text-[11px] text-muted-foreground tabular-nums">{t.label}</span>
                    <span className="h-2.5 w-px bg-muted-foreground" />
                  </div>
                );
              })}
            </div>

            {/* Lanes */}
            <div style={{ height: lanesH }} className="relative">
              {ticks.major.map((t) => (
                <div key={`g${t.ms}`} className="pointer-events-none absolute inset-y-0 w-px bg-border" style={{ left: `${pct(t.ms)}%` }} />
              ))}
              {lanes.map((l, i) => (
                <div key={l.key} className="absolute inset-x-0" style={{ top: i * LANE_H, height: LANE_H }}>
                  <div className="absolute inset-x-0 top-1 bottom-1 rounded-sm bg-muted" />
                  {/* Footage, coloured by why it was kept. */}
                  {(l.segments ? blocksFor(l.segments, view.start, view.end) : []).map((b) => {
                    const left = Math.max(0, pct(b.start));
                    const right = Math.min(100, pct(b.end));
                    return (
                      <div
                        key={`${b.start}-${b.kind}`}
                        className="absolute top-1.5 bottom-1.5 rounded-sm"
                        style={{
                          left: `${left}%`,
                          width: `max(2px, ${right - left}%)`,
                          background: colourOf(b.kind),
                        }}
                      />
                    );
                  })}
                  {/* Event / motion windows over it, full lane height so they read at a glance. */}
                  {(l.triggers ? windowsFor(l.triggers, view.start, view.end) : [])
                    .filter((w) => shown.has(w.kind))
                    .map((w) => {
                      const left = Math.max(0, pct(w.start));
                      const right = Math.min(100, pct(w.end));
                      const what = w.kind === 'event'
                        ? ([...w.types].map((t) => EVENT_TYPE_LABELS[t] || t).join(', ') || 'Event')
                        : TRIGGER_LABELS[w.kind];
                      return (
                        <Tooltip key={`w${w.start}-${w.kind}`} className="contents" label={`${what} · ${fmtTick(w.start, true)}–${fmtTick(w.end, true)}${w.count > 1 ? ` · ${w.count} triggers` : ''}`}>
                        <div
                          className="absolute top-0.5 bottom-0.5 rounded-sm"
                          style={{
                            left: `${left}%`,
                            width: `max(3px, ${right - left}%)`,
                            background: TRIGGER_COLOURS[w.kind],
                            boxShadow: `0 0 0 1px ${TRIGGER_COLOURS[w.kind]}`,
                          }}
                        />
                        </Tooltip>
                      );
                    })}
                </div>
              ))}
            </div>

            {/* Select mode says what to do ON the timeline, so the bar's toggle can stay an icon. */}
            {selectMode && (
              <div className="pointer-events-none absolute inset-x-0 top-1 z-30 flex justify-center">
                <span className="rounded-md bg-brand px-2 py-0.5 text-[10px] font-medium text-white shadow">
                  Drag across the timeline to select a range · Esc to cancel
                </span>
              </div>
            )}

            {/* Selection */}
            {selIn && (
              <div
                className="pointer-events-none absolute inset-y-0 z-10"
                style={{ left: `${Math.max(0, pct(selection!.start))}%`, width: `${Math.min(100, pct(selection!.end)) - Math.max(0, pct(selection!.start))}%` }}
              >
                <div className="absolute inset-0 bg-brand opacity-20" />
                <div className="absolute inset-y-0 left-0 w-0.5 bg-brand" />
                <div className="absolute inset-y-0 right-0 w-0.5 bg-brand" />
              </div>
            )}

            {/* What to do with the selection — a small toolbar over it, clamped to the track. */}
            {hasSelection && selection && (() => {
              const mid = clamp(pct((selection.start + selection.end) / 2), 0, 100);
              const align = mid < 15 ? 'left-0' : mid > 85 ? 'right-0' : '-translate-x-1/2';
              return (
                <div className="pointer-events-none absolute top-0.5 z-40" style={mid < 15 ? { left: 0 } : mid > 85 ? { right: 0 } : { left: `${mid}%` }}>
                  <div
                    className={`pointer-events-auto relative ${align === '-translate-x-1/2' ? '-translate-x-1/2' : ''} ${PILL} border-brand bg-card shadow-lg`}
                    onPointerDown={(e) => e.stopPropagation()}
                    onPointerMove={(e) => e.stopPropagation()}
                    onPointerUp={(e) => e.stopPropagation()}
                  >
                    <Tip label={`Selected ${fmtTick(selection.start, true)} – ${fmtTick(selection.end, true)} (${fmtSpan(selection.end - selection.start)})`}>
                      <span className="flex h-7 items-center gap-1 bg-brand-lighter px-1.5 font-mono text-[11px] text-brand tabular-nums">
                        {fmtTick(selection.start, span < 15 * 60_000)}–{fmtTick(selection.end, span < 15 * 60_000)}
                        <span className="font-sans opacity-80">{fmtSpan(selection.end - selection.start)}</span>
                      </span>
                    </Tip>
                    <Tip label="Zoom the timeline to the selection">
                      <button onClick={zoomToSelection} aria-label="Zoom to selection" className={SEG}>
                        <MagnifyingGlassPlusIcon className="h-3.5 w-3.5" />
                      </button>
                    </Tip>
                    {onPlayRange && (
                      <Tip label="Play only this range (makes it the time range)">
                        <button onClick={playSelection} aria-label="Play only this range (sets the time range)" className={SEG}>
                          <PlayIcon className="h-3.5 w-3.5" />
                        </button>
                      </Tip>
                    )}
                    {selectionActions?.(selection, () => setSelection(null))}
                    <Tip label="Clear the selection">
                      <button onClick={() => setSelection(null)} aria-label="Clear selection" className={SEG}>
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </Tip>
                  </div>
                </div>
              );
            })()}

            {/* Hover line + time */}
            {hover != null && draggingRef.current !== 'seek' && (
              <div className="pointer-events-none absolute inset-y-0 z-10 w-px bg-foreground opacity-40" style={{ left: `${pct(hover)}%` }}>
                <span className="absolute left-1 top-0.5 whitespace-nowrap rounded bg-black/80 px-1.5 py-0.5 font-mono text-[10px] text-white">
                  {fmtTick(hover, true)}
                </span>
              </div>
            )}

            {/* Playhead — through the axis and every lane */}
            {playheadIn && (
              <div className="pointer-events-none absolute inset-y-0 z-20" style={{ left: `${pct(timeMs)}%` }}>
                <div className="absolute inset-y-0 -ml-px w-0.5 bg-red-500" />
                <div className="absolute -ml-1.5 h-0 w-0 border-x-[6px] border-t-[8px] border-x-transparent border-t-red-500" style={{ top: AXIS_H - 8 }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Footer: legend (click to show / hide a kind) | overview bar ─── */}
      <div className="flex border-t border-border">
        <div className={`${LABEL_W} flex flex-none flex-wrap items-center gap-x-1 gap-y-0.5 border-r border-border px-2 py-1`}>
          {KINDS.map((k) => (
            <Tip key={k} label={shown.has(k) ? `Grey out ${TRIGGER_LABELS[k].toLowerCase()} recording` : `Colour ${TRIGGER_LABELS[k].toLowerCase()} recording again`}>
            <button
              onClick={() => toggleKind(k)}
              aria-pressed={shown.has(k)}
              className={`flex items-center gap-1 rounded px-1 py-0.5 text-[10px] transition-colors hover:bg-muted ${
                shown.has(k) ? 'text-foreground' : 'text-muted-foreground line-through opacity-60'
              }`}
            >
              <span className="h-2 w-2 rounded-sm" style={{ background: shown.has(k) ? TRIGGER_COLOURS[k] : NEUTRAL }} />
              {TRIGGER_LABELS[k]}
            </button>
            </Tip>
          ))}
        </div>
        <div className="flex min-w-0 flex-1 items-center px-3 py-1.5">
          <Tooltip label="Drag to move along the range" className="w-full">
          <div
            ref={overviewRef}
            onPointerDown={onOverviewDown}
            onPointerMove={onOverviewMove}
            onPointerUp={onOverviewUp}
            onPointerCancel={onOverviewUp}
            className="relative h-2.5 w-full cursor-grab rounded-full bg-muted active:cursor-grabbing"
          >
            <div
              className="absolute inset-y-0 rounded-full bg-brand"
              style={{
                left: `${((view.start - rangeStartMs) / fullSpan) * 100}%`,
                width: `max(8px, ${(span / fullSpan) * 100}%)`,
              }}
            />
            <div
              className="absolute -top-0.5 h-3.5 w-0.5 bg-red-500"
              style={{ left: `${clamp(((timeMs - rangeStartMs) / fullSpan) * 100, 0, 100)}%` }}
            />
          </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
